// Website pixels: the small presentational pieces the list and the detail
// drawer share — coverage pills, the lock primitives, the parameter reference
// table and the common-mistakes list.

import { Fragment } from 'react';
import type { ReactNode } from 'react';
import { INSTALL_PARAMS } from '@/api/types.gen';
import type { Pixel } from '@/api/types';
import { pixelCoverage } from '@/lib/pixel-draft';

// Only the providers a pixel is set up for, each in its colour; none reads as
// "No provider". The predicate is `pixelCoverage`, the one the Coverage filter
// and the "Dual coverage" figure count with, so the cell cannot disagree with
// them. (It used to show both labels always, the unset one as a grey outline,
// so a pixel with no provider at all read as covered by both.)
export function CoveragePills({ pixel }: { pixel: Pick<Pixel, 'meta_pixel_id' | 'ttd_adv_id' | 'ttd_ct_id'> }) {
  const cov = pixelCoverage(pixel);
  if (!cov.meta && !cov.ttd) {
    return <span className="muted pixel-coverage-none" style={{fontSize:11}}>No provider</span>;
  }
  return (
    <span style={{display:'inline-flex',gap:4,alignItems:'center'}}>
      {cov.meta && (
        <span className="pixel-coverage-tag" style={{
          fontFamily:'var(--font-mono)',fontSize:9.5,letterSpacing:'.08em',fontWeight:700,
          padding:'2px 6px',borderRadius:3,
          background:'color-mix(in oklch, var(--chart-1) 16%, transparent)',
          color:'var(--chart-1)',
          border:'1px solid color-mix(in oklch, var(--chart-1) 35%, transparent)',
        }}>META</span>
      )}
      {cov.ttd && (
        <span className="pixel-coverage-tag" style={{
          fontFamily:'var(--font-mono)',fontSize:9.5,letterSpacing:'.08em',fontWeight:700,
          padding:'2px 6px',borderRadius:3,
          background:'color-mix(in oklch, var(--chart-3) 18%, transparent)',
          color:'var(--chart-3)',
          border:'1px solid color-mix(in oklch, var(--chart-3) 35%, transparent)',
        }}>CTV/DISPLAY</span>
      )}
    </span>
  );
}

// Snippet building lives in <InstallSnippetBuilder/> (pixel-install.tsx),
// driven by the shared install catalog. The reference table below is built
// from the same catalog so the two can't drift.

export const COMMON_MISTAKES: readonly ReactNode[] = [
  <Fragment key="cents">Don't send cents as integer (send <code className="mono">129.50</code>, not <code className="mono">12950</code>).</Fragment>,
  <Fragment key="dec">Use <code className="mono">.</code> not <code className="mono">,</code> as decimal separator.</Fragment>,
  <Fragment key="iso"><code className="mono">currency</code> must be uppercase ISO 4217 (e.g. <code className="mono">USD</code>, not <code className="mono">usd</code>).</Fragment>,
  <Fragment key="case"><code className="mono">pixel_id</code> is case-sensitive (<code className="mono">summer25</code> ≠ <code className="mono">Summer25</code>).</Fragment>,
];

// Single parameter-reference table, one row per shared-catalog entry. The
// Server default column reflects THIS pixel's funnel defaults so an operator
// can see at a glance which snippet params the registry already covers.
export function ParamRefTable({ pixel }: { pixel: Pick<Pixel, 'default_production_id' | 'default_venue_id'> | null | undefined }) {
  const serverDefault = (key: string) =>
    key === 'production_id' ? (pixel?.default_production_id ?? null)
    : key === 'venue_id'    ? (pixel?.default_venue_id ?? null)
    : null;
  return (
    <div className="tbl-wrap" style={{margin:0,borderRadius:'var(--r-sm,4px)',border:'1px solid var(--line)'}}>
      <table className="tbl" style={{margin:0}}>
        <thead>
          <tr>
            <th style={{minWidth:96}}>Name</th>
            <th style={{minWidth:140}}>Shape</th>
            <th style={{minWidth:96}}>Example</th>
            <th style={{minWidth:90}}>Required for</th>
            <th style={{minWidth:110}}>Server default</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {/* Pseudo-params (explicit_track, consent_hook) toggle snippet blocks,
              not wire parameters — same filter as the /install reference table. */}
          {INSTALL_PARAMS.filter(p => p.sam_key != null || p.data_attr != null || p.key === 'pixel_id').map(p => {
            const sd = serverDefault(p.key);
            return (
              <tr key={p.key}>
                <td className="mono" style={{fontSize:11,fontWeight:600,whiteSpace:'nowrap'}}>{p.key}</td>
                <td className="mono" style={{fontSize:10.5,color:'var(--ink-2)',whiteSpace:'nowrap'}}>{p.shape}</td>
                <td className="mono" style={{fontSize:11,whiteSpace:'nowrap'}}>{p.example || '—'}</td>
                <td className="mono" style={{fontSize:10.5,whiteSpace:'nowrap'}}>{p.required_for.length ? p.required_for.join(', ') : '—'}</td>
                <td className="mono" style={{fontSize:11,whiteSpace:'nowrap'}}>{sd || '—'}</td>
                <td style={{whiteSpace:'normal',fontSize:11.5,lineHeight:1.35,color:'var(--ink-1)'}}>{p.description}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Small inline lock icon used to indicate provider integrations are protected.
export const LockIcon = ({ open }: { open?: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="7" width="10" height="7" rx="1"/>
    {open
      ? <path d="M5.5 7V4.5a2.5 2.5 0 0 1 5 0"/>
      : <path d="M5.5 7V4.5a2.5 2.5 0 0 1 5 0V7"/>}
    <circle cx="8" cy="10.5" r=".9" fill="currentColor" stroke="none"/>
  </svg>
);

interface LockToggleButtonProps {
  locked: boolean;
  onClick: () => void;
  title?: string;
}

// Section/field lock toggle — the small "Locked · Edit" / "Unlocked · Lock"
// button used by Provider integrations, Funnel defaults, and the ID field.
export function LockToggleButton({ locked, onClick, title }: LockToggleButtonProps) {
  return (
    <button
      className="btn btn-sm"
      onClick={onClick}
      title={title || (locked ? 'Unlock to edit' : 'Lock to prevent accidental edits')}
      style={locked
        ? {fontFamily:'var(--font-mono)',fontSize:10,letterSpacing:'.06em',textTransform:'uppercase',padding:'3px 8px'}
        : {background:'var(--warn)',color:'#1a1409',borderColor:'var(--warn)',fontFamily:'var(--font-mono)',fontSize:10,letterSpacing:'.06em',textTransform:'uppercase',padding:'3px 8px'}}
    >
      <span style={{display:'inline-flex',alignItems:'center',gap:5}}>
        <LockIcon open={!locked}/>
        {locked ? 'Locked · Edit' : 'Unlocked · Lock'}
      </span>
    </button>
  );
}

interface LockedValueProps {
  value: string | null | undefined;
  placeholder: string;
}

// Read-only field shell that surfaces a "Locked" lozenge instead of an input
// when the provider panel is in its protected state — this is what stops a
// distracted user from clearing a live pixel ID with a stray keystroke.
export function LockedField({ label, value, placeholder }: LockedValueProps & { label?: ReactNode }) {
  return (
    <div className="field" style={{marginBottom:8}}>
      {label && <label className="field-lbl">{label}</label>}
      <LockedValue value={value} placeholder={placeholder}/>
    </div>
  );
}

// The bare "locked value" lozenge — useful when you want to drop a locked
// view into an existing field wrapper without nesting another .field div.
export function LockedValue({ value, placeholder }: LockedValueProps) {
  return (
    <div className="mono" style={{
      display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,
      background:'var(--bg-sunk)',
      border:'1px solid var(--line)',
      borderRadius:'var(--r-sm,4px)',
      padding:'6px 10px',
      fontSize:12,
      color: value ? 'var(--ink-1)' : 'var(--ink-3)',
    }}>
      <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
        {value || <em style={{fontStyle:'normal',color:'var(--ink-3)'}}>{placeholder}</em>}
      </span>
      <span style={{display:'inline-flex',alignItems:'center',gap:4,color:'var(--ink-3)',fontFamily:'var(--font-mono)',fontSize:9.5,letterSpacing:'.08em',textTransform:'uppercase'}}>
        <LockIcon/> Locked
      </span>
    </div>
  );
}
